import React, { Fragment } from "react";
import Image from "next/image";
import imageUrlBuilder from "@sanity/image-url";
import { PortableText, PortableTextComponents } from "@portabletext/react";
import { merge, cloneDeep } from "lodash-es";
import { getImageDimensions } from "@sanity/asset-utils";
import { prodClient } from "./lib/client";
import { sanityImageUrl } from "@/lib/utils";
import arrowSvg from "@/public/global/svg/arrow.svg";
import blogQuote from "@/public/global/blogQuote.svg";
import quoteLightGreen from "@/public/global/quoteLightGreen.svg";

const sanityImageBuilder = imageUrlBuilder(prodClient);

export const richTextComponent: PortableTextComponents = {
  marks: {
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    greenSerifHighlight: ({ children }) => <span className="font-bold text-[#6DA991] font-gestura">{children}</span>,
    purpleSerifHighlight: ({ children }) => <span className="font-bold text-[#6D72A9] font-gestura">{children}</span>,
    lightGreenSerifHighlight: ({ children }) => (
      <span className="font-[600] text-[#A3DEB2] font-gestura">{children}</span>
    ),
    fontInstrument: ({ children }) => <span className="font-instrument">{children}</span>,
    fontGestura: ({ children }) => <span className="font-gestura">{children}</span>,
    fontGesturaFaded: ({ children }) => <span className="text-opacity-50 font-gestura faded">{children}</span>,
    greenTextNewStyle: ({ children }) => <span className="lg:text-[#6DA991]">{children}</span>,
    underline: ({ children }) => <u className="underline">{children}</u>,
    em: ({ children }) => <em className="pr-[.125em]">{children}</em>,
    inlineImage: ({ value }) => {
      const url = value?.image?.asset?._ref ? sanityImageUrl(value?.image?.asset?._ref) : "";
      const dimensions = value?.image?.asset?._ref
        ? getImageDimensions(value?.image?.asset?._ref)
        : {
            width: 0,
            height: 0,
            aspectRatio: 0,
          };
      return (
        <Image
          className="inline-block mx-2 inline-icon"
          src={{
            src: url,
            width: dimensions.width,
            height: dimensions.height,
          }}
          alt="Icon image"
          quality={100}
        />
      );
    },
    inlineCornerChip: ({ value, children }) => {
      const imageUrl = value?.image?.asset?._ref ? sanityImageUrl(value?.image?.asset?._ref) : null;
      const text = value?.text || "";

      return (
        <span className="rt-inlineCornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <span className="leading-none text-black font-geistmono text-[2rem] translate-y-[.125rem]">{text}</span>
        </span>
      );
    },
    lightGreenHighlightNewWebsite: ({ children }) => (
      <span
        style={{
          background: "linear-gradient(to top, #E2F3EF 50%, transparent 50%)",
          boxDecorationBreak: "clone",
          WebkitBoxDecorationBreak: "clone",
        }}
        className="text-[#003828]">
        {children}
      </span>
    ),

    darkGreenHighlightNewWebsite: ({ children }) => (
      <span
        style={{
          background: "linear-gradient(to top, #083628 45%, transparent 45%)",
          boxDecorationBreak: "clone",
          WebkitBoxDecorationBreak: "clone",
        }}
        className="text-[#D6EEE7]">
        {children}
      </span>
    ),
    serifNewWebsite: ({ children }) => <span className="font-blacker font-[400]">{children} </span>,
  },
  block: {
    h1: ({ children }) => <h1 className="">{children}</h1>,
    h2: ({ children }) => <h2 className="">{children}</h2>,
    h3: ({ children }) => <h3 className="">{children}</h3>,
    h4: ({ children }) => <h4 className="">{children}</h4>,
    h5: ({ children }) => <h5 className="">{children}</h5>,
    h6: ({ children }) => <h6 className="">{children}</h6>,
    normal: ({ children }) => <p className="font-geist text-black/70">{children}</p>,
    medium: ({ children }) => <p className="text-md">{children}</p>,
  },
  types: {
    image: ({ value }) => {
      return (
        <Image
          className="rounded-[1.25rem]"
          src={{
            src: sanityImageBuilder.image(value).url(),
            width: getImageDimensions(value)?.width,
            height: getImageDimensions(value)?.height,
          }}
          alt={value?.alt || ""}
        />
      );
    },
    imageWithDescription: ({ value }) => {
      const imageAsset = value?.asset;
      if (!imageAsset) return null;
      return (
        <div className="mb-[3rem]">
          <Image
            className="rounded-[1.25rem] mb-[1rem]"
            src={{
              src: sanityImageBuilder.image(imageAsset).url(),
              width: getImageDimensions(imageAsset)?.width,
              height: getImageDimensions(imageAsset)?.height,
            }}
            alt={value?.alt || ""}
          />
          {value?.alt && <span className="text-[#09323ACC] text-[1.75rem]">{value?.alt}</span>}
        </div>
      );
    },
    imageDesktop: ({ value }) => {
      const imageAsset = value?.asset;
      if (!imageAsset) return null;
      return (
        <Image
          className="hidden w-full lg:block rounded-[1.25rem]"
          src={{
            src: sanityImageBuilder.image(imageAsset).url(),
            width: getImageDimensions(imageAsset)?.width,
            height: getImageDimensions(imageAsset)?.height,
          }}
          alt={value?.alt || ""}
        />
      );
    },
    imageMobile: ({ value }) => {
      const imageAsset = value?.asset;
      if (!imageAsset) return null;
      return (
        <Image
          className="lg:hidden rounded-[1.25rem]"
          src={{
            src: sanityImageBuilder.image(imageAsset).url(),
            width: getImageDimensions(imageAsset)?.width,
            height: getImageDimensions(imageAsset)?.height,
          }}
          alt={value?.alt || ""}
        />
      );
    },
    solidLine: ({ value }) => {
      const marginClass = value?.marginSize || "my-[4rem]";

      return <hr className={`w-full border-t border-white border-opacity-20 ${marginClass}`} />;
    },
    dashedLine: ({ value }) => {
      const marginClass = value?.marginSize || "my-[3rem] lg:my-[4rem]";

      return (
        <hr
          className={`w-full border-0 h-[1px] lg:h-[2px] ${marginClass}`}
          style={{
            backgroundImage:
              "repeating-linear-gradient(to right, #10303733 0, #10303733 .75rem, transparent .75rem, transparent 1.5rem)",
          }}
        />
      );
    },
    dividerLine: ({ value }) => {
      const marginClass = value?.marginSize || "my-[2.25rem]";

      return (
        <div className={`relative w-full overflow-clip flex items-center h-[0.75rem] ${marginClass}`}>
          <div className="absolute left-0 top-1/2 rotate-45 -translate-x-1/2 -translate-y-1/2 w-[0.75rem] h-[0.75rem] bg-[#E7EAEB]" />
          <div className="w-full bg-[#E7EAEB] h-[0.125rem]" />
          <div className="absolute right-0 top-1/2 rotate-45 translate-x-1/2 -translate-y-1/2 w-[0.75rem] h-[0.75rem] bg-[#E7EAEB]" />
        </div>
      );
    },
    cornerList: ({ value }) => {
      return (
        <ul className="flex flex-col items-start p-0 list-none cornerList mb-[1.25em] gap-[1rem]">
          {value?.items?.map((item: any, index: number) => {
            return (
              <li
                key={index}
                className="flex items-center p-0 m-0 gap-[1rem]">
                <div className="cornerListCorner bg-[#112628] flex-shrink-0 w-[1.5rem] h-[1.5rem] [clip-path:polygon(0_0,calc(100%-.75rem)_0,100%_.75rem,100%_100%,0_100%)]" />
                <div className="richtext richtext-start prose-p:leading-[1.1em]">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      );
    },
    cornerChip: ({ value }) => {
      const imageUrl = value?.image?.asset?._ref ? sanityImageUrl(value?.image?.asset?._ref) : null;
      const text = value?.text || "";

      return (
        <div className="rt-cornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)] prose-p:text-black">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <p className="mb-0 leading-none font-geistmono text-[2rem] translate-y-[.125rem]">{text}</p>
        </div>
      );
    },
    numberedList: ({ value }) => {
      const items = value?.items || [];
      const startNumber = value?.startNumber || 1;

      return (
        <ol
          className="flex flex-col items-start p-0 list-none numberedList mb-[1.25rem] gap-[1rem]"
          start={startNumber}>
          {items.map((item: any, index: number) => {
            return (
              <li
                key={index}
                className="flex items-center p-0 m-0 gap-[.625rem]">
                <span className="flex-shrink-0 text-[#D9F9B9] font-geist min-w-[2rem]">{startNumber + index}.</span>

                <div className="richtext richtext-start">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      );
    },
    iconItem: ({ value }) => {
      const iconUrl = value?.icon?.asset?._ref ? sanityImageUrl(value?.icon?.asset?._ref) : null;
      const title = value?.title || "";

      return (
        <div className="flex flex-col items-start iconItem gap-[1rem] mb-[1rem]">
          <div className="flex items-center gap-[2.25rem]">
            {iconUrl && (
              <Image
                className="!w-[5rem] !h-[5rem] !m-0 flex-shrink-0"
                src={iconUrl}
                width={32}
                height={32}
                alt=""
              />
            )}
            {title && <p className="m-0 text-[#1C1F22] text-[2.5rem]">{title}</p>}
          </div>
          <div className="flex-1">
            <div className="richtext richtext-start prose-p:text-[2.25rem] prose-strong:font-[600]">
              <PortableText
                value={value?.text}
                components={richTextComponent}
              />
            </div>
          </div>
        </div>
      );
    },
    arrowBullet: ({ value }) => {
      const imageUrl = value?.image?.asset?._ref ? sanityImageUrl(value?.image?.asset?._ref) : null;

      return (
        <div className="flex items-start arrowBullet gap-[1rem] mb-[1rem]">
          {imageUrl && (
            <Image
              className="!w-[3.25rem] !h-[3.25rem] !m-0 flex-shrink-0"
              src={imageUrl}
              width={32}
              height={32}
              alt=""
            />
          )}
          <div className="flex-1 richtext richtext-start prose-p:text-[2.25rem] prose-strong:font-[600]">
            <PortableText
              value={value?.text}
              components={richTextComponent}
            />
          </div>
        </div>
      );
    },
    quoteBlock: ({ value }) => {
      return (
        <div className="flex flex-col items-start quoteBlock gap-[1rem] mb-[1rem] bg-[#09323A] rounded-[1.5rem] p-[5.75rem] lg:gap-[3rem]">
          <div className="flex items-center gap-[3rem]">
            <Image
              className="!w-[3.25rem] lg:!w-[5.875rem] !m-0 flex-shrink-0"
              src={blogQuote}
              alt=""
            />
            <span className="text-[2rem] text-white/60">{value?.name}</span>
          </div>

          <div className="flex-1 richtext richtext-start prose-p:!text-[3rem] prose-p:!font-display prose-p:!text-white prose-p:!text-opacity-100 prose-strong:!font-[600]">
            <PortableText
              value={value?.text}
              components={richTextComponent}
            />
          </div>
        </div>
      );
    },
    quoteBlockLight: ({ value }) => {
      return (
        <div className="flex flex-col items-start w-full lg:flex-row quoteBlockLight gap-[3rem] mb-[1rem] bg-[#1030370D] rounded-[1.5rem] px-[3.5rem] py-[3rem] lg:px-[6.25rem] lg:py-[2.5rem]">
          <div className="flex items-center gap-[3rem]">
            <Image
              className="!w-[2.75rem] !m-0 flex-shrink-0"
              src={quoteLightGreen}
              alt=""
            />
          </div>

          <div className="flex-1 richtext richtext-start tracking-[-0.075rem] lg:tracking-[-0.09rem] prose-p:!text-[2.5rem] lg:prose-p:!text-[3rem] prose-p:!font-[500] prose-p:!font-instrument-sans prose-p:!text-[#103037] prose-p:!text-opacity-100">
            <PortableText
              value={value?.text}
              components={richTextComponent}
            />
          </div>
        </div>
      );
    },
    greenBlock: ({ value }) => {
      return (
        <div
          className="flex flex-col items-start greenBlock mb-[1rem] bg-[#F5F8F6] rounded-[1.5rem] p-[4.5rem]
          [&>*:first-child]:mt-0 prose-p:text-[2rem]">
          {/*<div className="flex-1 richtext richtext-start prose-p:text-[3rem] prose-p:font-display prose-p:text-white prose-p:text-opacity-100 prose-strong:font-[600]">*/}
          <PortableText
            value={value?.text}
            components={richTextComponent}
          />
          {/*</div>*/}
        </div>
      );
    },
    squareTopicList: ({ value }) => {
      const items = value?.items || [];

      return (
        <ul className="flex flex-col items-start p-0 list-none numberedList mb-[1.25rem] gap-[3rem]">
          {items.map((item: any, index: number) => {
            return (
              <Fragment key={index}>
                <li className="flex items-center p-0 m-0 gap-[3rem]">
                  {!value.hideBullet && <span className="flex-shrink-0 bg-[#6DA991] w-[1rem] h-[1rem]"></span>}

                  <div className="richtext richtext-start">
                    <PortableText
                      value={item.content}
                      components={richTextComponent}
                    />
                  </div>
                </li>
                {index !== items.length - 1 && <hr className="my-0 w-full border-t border-[#09323A]/10" />}
              </Fragment>
            );
          })}
        </ul>
      );
    },
    squareTopicListNoDivisor: ({ value }) => {
      const items = value?.items || [];

      return (
        <ul className="flex flex-col items-start p-0 list-none squareTopicListNoDivisor gap-[1.25rem]">
          {items.map((item: any, index: number) => {
            return (
              <Fragment key={index}>
                <li className="flex items-center p-0 m-0 gap-[1rem] topicItem">
                  <span className="bg-[#6DA991] w-[0.5rem] h-[0.5rem] shrink-0 topicItemSquare"></span>

                  <div className="richtext richtext-start prose-p:text-black prose-p:text-[2.25rem] prose-p:font-medium">
                    <PortableText
                      value={item.content}
                      components={richTextComponent}
                    />
                  </div>
                </li>
              </Fragment>
            );
          })}
        </ul>
      );
    },
    greenLineLeftBlock: ({ value }) => {
      return (
        <div
          className="mt-[3rem] flex flex-col items-start greenLineLeftBlock mb-[1rem] border-l border-[#85D0A6] pl-[3.75rem]
          [&>*:first-child]:mt-0">
          <PortableText
            value={value?.text}
            components={richTextComponent}
          />
        </div>
      );
    },
    whiteBlock: ({ value }) => {
      return (
        <div
          className="flex flex-col items-start greenBlock mb-[4rem] bg-white rounded-[2rem] p-[3rem]
          [&>*:first-child]:mt-0 [&>*:only-child]:mb-0">
          {/*<div className="flex-1 richtext richtext-start prose-p:text-[3rem] prose-p:font-display prose-p:text-white prose-p:text-opacity-100 prose-strong:font-[600]">*/}
          <PortableText
            value={value?.text}
            components={richTextComponent}
          />
          {/*</div>*/}
        </div>
      );
    },
    darkGreenBlock: ({ value }) => {
      return (
        <div
          className="flex flex-col items-start darkGreenBlock mb-[1rem] bg-[#18363C] rounded-[1.5rem] px-[3.5rem] lg:px-[4.5rem] py-[3rem]
          [&>*:first-child]:mt-0 prose-p:text-[2rem]">
          {/*<div className="flex-1 richtext richtext-start prose-p:text-[3rem] prose-p:font-display prose-p:text-white prose-p:text-opacity-100 prose-strong:font-[600]">*/}
          <PortableText
            value={value?.text}
            components={richTextComponent}
          />
          {/*</div>*/}
        </div>
      );
    },
    greenWithBorderBlock: ({ value }) => {
      return (
        <div
          className="flex flex-col items-start greenWithBorderBlock mb-[1rem] bg-[#6DA99124] border border-[#6DA991A6] rounded-[1.5rem] px-[3.5rem] lg:px-[4.5rem] py-[3rem]
          [&>*:first-child]:mt-0 prose-p:text-[2rem]">
          {/*<div className="flex-1 richtext richtext-start prose-p:text-[3rem] prose-p:font-display prose-p:text-white prose-p:text-opacity-100 prose-strong:font-[600]">*/}
          <PortableText
            value={value?.text}
            components={richTextComponent}
          />
          {/*</div>*/}
        </div>
      );
    },
  },
  list: {
    bullet: ({ children }) => <ul className="my-4 space-y-4 text-[#103037]/70">{children}</ul>,
    number: ({ children }) => <ol className="my-4 space-y-4 text-[#103037]/70">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="flex flex-row items-center leading-[3rem]">
        <Image
          src={arrowSvg}
          alt="arrow"
          className="flex-shrink-0 my-0 mr-[1.5rem] w-[2.35313rem]"
          width={27}
          height={27}
        />
        <span>{children}</span>
      </li>
    ),
    number: ({ children, index }) => (
      <li className="flex items-start leading-[3rem]">
        <span className="flex flex-shrink-0 justify-center items-center leading-none text-white text-[1.75rem] px-[0.75rem] py-[1rem] bg-[#103037] rounded-[0.66rem] mr-[1.5rem] min-w-[3.25rem]">
          {String((index ?? 0) + 1).padStart(2, "0")}
        </span>
        <span>{children}</span>
      </li>
    ),
  },
};

export const customRichTextComponent = (props: PortableTextComponents) => {
  return merge(cloneDeep(richTextComponent), props);
};
